export const CURRENCIES = ["USD", "PHP", "AUD", "CAD", "EUR", "GBP", "SGD"] as const;
export type CurrencyCode = (typeof CURRENCIES)[number];

export const PAYMENT_QR_METHODS = ["gcash", "maribank"] as const;
export type PaymentQrMethod = (typeof PAYMENT_QR_METHODS)[number];

export const WORKSPACE_ROLES = ["owner", "admin", "member"] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export const INVOICE_STATUSES = ["draft", "sent", "paid", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export type InvoiceDisplayStatus = InvoiceStatus | "overdue";

export const BILLING_CYCLES = ["one_time", "weekly", "biweekly", "monthly", "custom"] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export const BILLING_ITEM_TYPES = ["hourly", "fixed", "quantity"] as const;
export type BillingItemType = (typeof BILLING_ITEM_TYPES)[number];

export const RATE_UNIT_PRESETS = [
  "hour",
  "day",
  "week",
  "month",
  "project",
  "item",
  "page",
  "post",
  "funnel",
  "automation",
  "training_batch",
  "custom",
] as const;
export type RateUnitPreset = (typeof RATE_UNIT_PRESETS)[number];

export const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export type DueRule =
  | { kind: "none" }
  | { kind: "upon_receipt" }
  | { kind: "specific"; date: string }
  | { kind: "net"; days: number }
  | { kind: "weekly"; weekday: Weekday }
  | { kind: "every_n_weeks"; intervalWeeks: number; anchorDate: string }
  | { kind: "monthly"; dayOfMonth: number }
  | { kind: "custom"; label: string };

export interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface PartyInfo {
  name: string;
  company: string;
  email: string;
  phone: string;
  address: Address;
}

export interface CompanyProfile extends PartyInfo {
  tagline: string;
  website: string;
}

export interface PaymentQrSettings {
  gcashEnabled: boolean;
  maribankEnabled: boolean;
  preferred: PaymentQrMethod;
}

export interface PaymentQrAssetMeta {
  method: PaymentQrMethod;
  label: string;
  accountLabel: string;
  imageUrl: string | null;
  aspectRatio: number;
  hasImage: boolean;
}

export type PaymentQrAssetMap = Record<PaymentQrMethod, PaymentQrAssetMeta>;

export interface ClientDefaults {
  currency: CurrencyCode;
  billingCycle: BillingCycle;
  billingType: BillingItemType;
  rateUnit: string;
  rateCents: number;
  /**
   * Legacy compatibility field retained while older local and remote records
   * are upgraded. New UI flows use rateCents + billingType + rateUnit.
   */
  hourlyRateCents: number;
  dueRule: DueRule;
  paymentTerms: string;
  paymentInstructions: string;
}

export interface ClientProfile extends PartyInfo {
  id: string;
  defaults: ClientDefaults;
  createdAt: string;
  updatedAt: string;
}

export type ClientInput = Omit<ClientProfile, "id" | "createdAt" | "updatedAt">;

export interface InvoiceItemInput {
  id: string;
  description: string;
  billingType: BillingItemType;
  durationSeconds: number;
  hourlyRateCents: number;
  quantityMilli: number;
  unitLabel: string;
  unitPriceCents: number;
  fixedAmountCents: number;
}

export interface InvoiceItem extends InvoiceItemInput {
  sortOrder: number;
  amountCents: number;
}

export interface InvoiceInput {
  clientId: string | null;
  company: CompanyProfile;
  client: PartyInfo;
  issueDate: string;
  serviceStartDate: string;
  serviceEndDate: string;
  dueRule: DueRule;
  billingCycle: BillingCycle;
  currency: CurrencyCode;
  taxRateBps: number;
  paymentTerms: string;
  paymentInstructions: string;
  paymentQr: PaymentQrSettings;
  publicNotes: string;
  internalNotes: string;
  items: InvoiceItemInput[];
}

export interface PaymentInput {
  paidAt: string;
  method: string;
  reference: string;
}

export interface PaymentRecord extends PaymentInput {
  id: string;
  invoiceId: string;
  amountCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface ShareSettings {
  enabled: boolean;
  token: string | null;
  url: string | null;
  expiresAt: string | null;
  pinProtected: boolean;
  viewCount: number;
  lastViewedAt: string | null;
}

export interface InvoiceTotals {
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  amountPaidCents: number;
  balanceCents: number;
}

export interface InvoiceRecord extends InvoiceInput {
  id: string;
  invoiceNumber: string;
  dueDate: string | null;
  dueLabel: string;
  status: InvoiceStatus;
  items: InvoiceItem[];
  payments: PaymentRecord[];
  share: ShareSettings;
  totals: InvoiceTotals;
  paymentQrAssets: PaymentQrAssetMap;
  sentAt: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceListItem {
  id: string;
  invoiceNumber: string;
  clientName: string;
  clientCompany: string;
  issueDate: string;
  serviceStartDate: string;
  serviceEndDate: string;
  dueDate: string | null;
  dueLabel: string;
  billingCycle: BillingCycle;
  currency: CurrencyCode;
  status: InvoiceStatus;
  totals: InvoiceTotals;
  share: ShareSettings;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardSummary {
  totalInvoices: number;
  draftCount: number;
  sentCount: number;
  overdueCount: number;
  paidCount: number;
  outstandingCentsByCurrency: Partial<Record<CurrencyCode, number>>;
  paidCentsByCurrency: Partial<Record<CurrencyCode, number>>;
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  role: WorkspaceRole;
}

export interface WorkspaceSettings {
  workspace: WorkspaceSummary;
  company: CompanyProfile;
  defaultCurrency: CurrencyCode;
  invoicePrefix: string;
  invoicePadding: number;
  paymentQrAssets: PaymentQrAssetMap;
}

export interface WorkspaceQrInput {
  accountLabel: string;
  imageDataUrl?: string | null;
  aspectRatio: number;
}

export interface WorkspaceSettingsInput {
  workspaceName: string;
  company: CompanyProfile;
  defaultCurrency: CurrencyCode;
  invoicePrefix: string;
  invoicePadding: number;
  paymentQr: {
    gcash: WorkspaceQrInput;
    maribank: WorkspaceQrInput;
  };
}

export interface PlatformWorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string;
  createdAt: string;
  active: boolean;
  protected: boolean;
  clientCount: number;
  invoiceCount: number;
}

export interface ProvisionWorkspaceInput {
  name: string;
  ownerEmail: string;
  defaultCurrency: CurrencyCode;
}

export interface UpdatePlatformWorkspaceInput {
  name: string;
  ownerEmail: string;
}

export interface SetPlatformWorkspaceActiveInput {
  active: boolean;
}

export interface BootstrapPayload {
  company: CompanyProfile;
  clients: ClientProfile[];
  adminEmail: string;
  platformOwner: boolean;
  workspace: WorkspaceSummary;
  workspaceSettings: WorkspaceSettings;
}

export interface PublicInvoicePayload {
  invoice: InvoiceRecord;
  requiresPin: boolean;
}

export interface ShareInput {
  enabled: boolean;
  regenerate: boolean;
  expiresAt: string | null;
  pin: string | null;
}
